import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import {
  ChartFilterBar,
  GapPercentEvolutionCard,
  SectorHorizontalBarCard,
  SectorStrategicMapCard,
  SubsectorSummaryTableCard,
} from "../../../components/Charts";
import type { FilterDefinition, FilterValues } from "../../../types/chartFilters";
import type { FilterOnChange } from "../../../components/Charts/ChartFilterBar";
import { makeYearRangeFilter, P2_SISTEMA_OPTIONS } from "../common/sharedFilterDefs";
import { useP2Data, type P2FilterValues } from "./useP2ChartData";
import CompareButton from "../common/CompareButton";

const DEFAULT_FILTERS: P2FilterValues = {
  yearRange:     undefined,
  sectorsSistema: "Catalunya",
  mapaSistema:    "Catalunya",
  ambit:         "",
};

const P2ChartSection = () => {
  const [filters, setFilters] = useState<P2FilterValues>(DEFAULT_FILTERS);
  const {
    chart1Data,
    chart1Lines,
    chart1SubsectorData,
    chart1SubsectorLines,
    chart1MapData,
    chart1MapLines,
    chart2Data,
    availableAmbits,
    loading,
    error,
  } =
    useP2Data(filters);

  useEffect(() => {
    if (availableAmbits.length > 0 && !filters.ambit) {
      const defaultAmbit =
        availableAmbits.find(
          (ambit) => ambit.localeCompare("Indústria", "ca", { sensitivity: "base" }) === 0
        ) ?? availableAmbits[0];
      setFilters((prev) => ({
        ...prev,
        ambit: defaultAmbit,
      }));
    }
  }, [availableAmbits, filters.ambit]);

  const ambitOptions = useMemo(
    () => availableAmbits.map((a) => ({ value: a, label: a })),
    [availableAmbits]
  );

  const yearRangeFilters: FilterDefinition[] = useMemo(
    () => [makeYearRangeFilter(2015, 2024)],
    []
  );

  const handleFilterChange: FilterOnChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }) as P2FilterValues);
  };

  return (
    <>
      <ChartFilterBar
        filters={yearRangeFilters}
        values={filters as unknown as FilterValues}
        onChange={handleFilterChange}
        disabled={loading}
      />
      <SectorHorizontalBarCard
        title="Vista general per sectors"
        data={chart1Data}
        xKey="any"
        series={chart1Lines}
        loading={loading}
        error={error}
        headerExtra={
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              value={filters.sectorsSistema}
              options={P2_SISTEMA_OPTIONS}
              onChange={(v) => setFilters((prev) => ({ ...prev, sectorsSistema: v }))}
              style={{ width: 160 }}
              disabled={loading}
            />
          </div>
        }
      />
      <SectorStrategicMapCard
        title="Mapa impacte-moviment per sectors"
        data={chart1MapData}
        xKey="any"
        series={chart1MapLines}
        loading={loading}
        error={error}
        headerExtra={
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              value={filters.mapaSistema}
              options={P2_SISTEMA_OPTIONS}
              onChange={(v) => setFilters((prev) => ({ ...prev, mapaSistema: v }))}
              style={{ width: 160 }}
              disabled={loading}
            />
          </div>
        }
      />
      <SubsectorSummaryTableCard
        title="Detall de subsectors de serveis"
        data={chart1SubsectorData}
        xKey="any"
        series={chart1SubsectorLines}
        loading={loading}
        error={error}
      />
      <GapPercentEvolutionCard
        title="Bretxa percentual Catalunya vs Europa per sector"
        data={chart2Data}
        xKey="any"
        baseKey="Europa"
        compareKey="Catalunya"
        loading={loading}
        error={error}
        headerExtra={
          <Select
            value={filters.ambit || undefined}
            options={ambitOptions}
            placeholder="Sector"
            onChange={(v) => setFilters((prev) => ({ ...prev, ambit: v }))}
            style={{ width: 280 }}
            disabled={loading}
          />
        }
      />
      <CompareButton />
    </>
  );
};

export default P2ChartSection;
